import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, TextField } from "@material-ui/core";
import styled from "styled-components";
import { BookingContentName, BookingContentWrap } from "../MyBooking/styled/DesStyled";
import { BagSizeItem, getBagContent, saveBagContent, uploadBagImage } from "../../../services/BagContentService";
import ImageUploadInput from "../../controls/components/imageUploadInput/ImageUploadInput";
import { ImageInputData, UploadInputType } from "../../hooks/useImageUploadInput";
import { resolveAssetUrl } from "../../../utils/assetUrl";

/* ──────── styled components ──────── */
const PageCard = styled.div`
	max-width: 880px;
	margin: 0 auto;
	padding: 32px 36px 40px;
	background: #fff;
	border-radius: 12px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
`;

const PageSubtitle = styled.p`
	font-size: 13px;
	color: #6b7280;
	margin: 0 0 28px;
`;

const SizeCard = styled.div`
	border: 1px solid #e5e7eb;
	border-radius: 10px;
	padding: 20px 24px;
	margin-bottom: 20px;
	background: #fafafa;
	transition: border-color 0.15s;
	&:hover {
		border-color: #c7d2fe;
	}
`;

const SizeLabel = styled.div`
	font-size: 11px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: #6366f1;
	margin-bottom: 16px;
	display: flex;
	align-items: center;
	gap: 8px;
`;

const SizeBadge = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: #ede9fe;
	color: #4f46e5;
	border-radius: 6px;
	padding: 2px 10px;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.06em;
`;

const FieldsRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 14px;
	margin-bottom: 16px;
	@media screen and (max-width: 640px) {
		grid-template-columns: 1fr;
	}
`;

const ImageRow = styled.div`
	display: grid;
	grid-template-columns: auto 1fr auto;
	gap: 14px;
	align-items: flex-start;
	@media screen and (max-width: 640px) {
		grid-template-columns: 1fr;
	}
`;

const ImageUrlWrap = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const FieldHint = styled.div`
	font-size: 11px;
	color: #9ca3af;
	margin-top: 2px;
`;

const PreviewCard = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	background: #fff;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 12px 16px;
	min-width: 120px;
	max-width: 148px;
`;

const PreviewImg = styled.img`
	width: 52px;
	height: 52px;
	object-fit: contain;
`;

const PreviewTitle = styled.div`
	font-size: 12px;
	font-weight: 700;
	color: #111827;
	text-align: center;
	line-height: 1.3;
	word-break: break-word;
`;

const PreviewDesc = styled.div`
	font-size: 11px;
	color: #6b7280;
	text-align: center;
	line-height: 1.4;
	word-break: break-word;
`;

const PreviewLabel = styled.div`
	font-size: 10px;
	font-weight: 600;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: #9ca3af;
	margin-bottom: 4px;
`;

const OrDivider = styled.div`
	font-size: 11px;
	color: #9ca3af;
	font-weight: 600;
	padding-top: 28px;
`;

const StickySaveRow = styled.div`
	position: sticky;
	bottom: 0;
	z-index: 4;
	margin: 24px -36px -40px;
	padding: 12px 36px 16px;
	background: linear-gradient(180deg, rgba(255, 255, 255, 0.72) 0%, #fff 28%);
	backdrop-filter: blur(4px);
	border-top: 1px solid #e5e7eb;
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 12px;
`;

const SaveButton = styled.button<{ disabled?: boolean }>`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 10px 28px;
	border: none;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 600;
	color: #fff;
	background: ${(p) => (p.disabled ? "#9ca3af" : "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)")};
	cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
	transition: opacity 0.15s, transform 0.1s;
	box-shadow: 0 2px 8px rgba(37, 99, 235, 0.18);
	&:hover:not(:disabled) {
		opacity: 0.92;
		transform: translateY(-1px);
	}
	&:active:not(:disabled) {
		transform: translateY(0);
	}
`;

const StatusMsg = styled.div<{ variant: "success" | "error" }>`
	font-size: 13px;
	font-weight: 500;
	color: ${(p) => (p.variant === "success" ? "#15803d" : "#b91c1c")};
	background: ${(p) => (p.variant === "success" ? "#f0fdf4" : "#fef2f2")};
	border: 1px solid ${(p) => (p.variant === "success" ? "#bbf7d0" : "#fecaca")};
	border-radius: 6px;
	padding: 8px 14px;
`;

const UploadSpinner = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 12px;
	color: #6b7280;
	padding-top: 28px;
`;

/* ──────── SIZE_META ──────── */
const SIZE_META: Record<string, { label: string; icon: string }> = {
	small: { label: "Small", icon: "fa fa-shopping-bag" },
	medium: { label: "Medium", icon: "fa fa-suitcase" },
	large: { label: "Large", icon: "fa fa-cube" },
};

/* ──────── helpers ──────── */
function fileToImageData(file: File): Promise<ImageInputData> {
	return new Promise((resolve) => {
		const reader = new FileReader();
		reader.onload = (e) =>
			resolve({
				isExisted: false,
				domData: file,
				urlData: e.target?.result ?? null,
			});
		reader.readAsDataURL(file);
	});
}

/* ──────── SizeRow ──────── */
interface SizeRowProps {
	item: BagSizeItem;
	uploading: boolean;
	onTitleChange: (val: string) => void;
	onDescChange: (val: string) => void;
	onImageUrlChange: (val: string) => void;
	onUploadFile: (file: File) => Promise<void>;
}

function SizeRow({ item, uploading, onTitleChange, onDescChange, onImageUrlChange, onUploadFile }: SizeRowProps) {
	const meta = SIZE_META[item.key] ?? { label: item.key, icon: "fa fa-archive" };

	const [imageDatas, setImageDatas] = useState<ImageInputData[]>([]);
	const prevKey = useRef(item.key);

	// Reset local imageDatas when the size key changes (shouldn't happen, but be safe)
	useEffect(() => {
		if (prevKey.current !== item.key) {
			setImageDatas([]);
			prevKey.current = item.key;
		}
	}, [item.key]);

	const handleImageDatasChange = async (datas: ImageInputData[]) => {
		setImageDatas(datas);
		if (datas.length === 0) return;
		const latest = datas[datas.length - 1];
		if (!latest.domData) return;
		await onUploadFile(latest.domData as File);
		// clear local preview after upload so the URL field is the source of truth
		setImageDatas([]);
	};

	const previewSrc = item.image ? resolveAssetUrl(item.image) : "";

	return (
		<SizeCard>
			<SizeLabel>
				<i className={meta.icon} />
				<SizeBadge>{meta.label}</SizeBadge>
				<span style={{ color: "#9ca3af", fontWeight: 400 }}>bag size</span>
			</SizeLabel>

			<FieldsRow>
				<div>
					<TextField
						label="Title"
						value={item.title}
						onChange={(e) => onTitleChange(e.target.value)}
						variant="outlined"
						size="small"
						fullWidth
						inputProps={{ maxLength: 80 }}
					/>
				</div>
				<div>
					<TextField
						label="Description"
						value={item.description}
						onChange={(e) => onDescChange(e.target.value)}
						variant="outlined"
						size="small"
						fullWidth
						inputProps={{ maxLength: 160 }}
					/>
				</div>
			</FieldsRow>

			<ImageRow>
				<div>
					<FieldHint style={{ marginBottom: 6 }}>Upload file</FieldHint>
					{uploading ? (
						<UploadSpinner>
							<CircularProgress size={14} />
							Uploading...
						</UploadSpinner>
					) : (
						<ImageUploadInput
							type={UploadInputType.Single}
							imageDatas={imageDatas}
							onImageDatasChange={handleImageDatasChange}
							id={`bag-img-upload-${item.key}`}
							btnText="Choose image"
						/>
					)}
				</div>

				<OrDivider>or</OrDivider>

				<ImageUrlWrap>
					<TextField
						label="Image URL"
						value={item.image}
						onChange={(e) => onImageUrlChange(e.target.value)}
						variant="outlined"
						size="small"
						fullWidth
						placeholder="https://... or /static/img/..."
						inputProps={{ maxLength: 512 }}
					/>
					<FieldHint>Paste a URL, or use the upload above. Upload fills this field automatically.</FieldHint>
				</ImageUrlWrap>

				<div>
					<PreviewLabel>Preview</PreviewLabel>
					<PreviewCard>
						{previewSrc ? (
							<PreviewImg
								src={previewSrc}
								alt={item.title}
								onError={(e) => {
									(e.target as HTMLImageElement).style.display = "none";
								}}
							/>
						) : (
							<div
								style={{
									width: 52,
									height: 52,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									color: "#d1d5db",
									fontSize: 24,
								}}>
								<i className="fa fa-image" />
							</div>
						)}
						<PreviewTitle>{item.title || <span style={{ color: "#d1d5db" }}>Title</span>}</PreviewTitle>
						<PreviewDesc>
							{item.description || <span style={{ color: "#d1d5db" }}>Description</span>}
						</PreviewDesc>
					</PreviewCard>
				</div>
			</ImageRow>
		</SizeCard>
	);
}

/* ──────── Main Page ──────── */
export default function BagContentPage() {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [sizes, setSizes] = useState<BagSizeItem[]>([]);
	const [uploadingKey, setUploadingKey] = useState<string | null>(null);
	const [status, setStatus] = useState<{ variant: "success" | "error"; msg: string } | null>(null);

	const isUploading = uploadingKey !== null;

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				setLoading(true);
				const data = await getBagContent();
				if (mounted) setSizes(data);
			} catch (err: any) {
				if (mounted)
					setStatus({
						variant: "error",
						msg: `Failed to load config: ${
							err?.response?.data?.message || err?.message || "Please try again"
						}`,
					});
			} finally {
				if (mounted) setLoading(false);
			}
		})();
		return () => {
			mounted = false;
		};
	}, []);

	const updateSize = (key: string, patch: Partial<BagSizeItem>) => {
		setSizes((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
	};

	const handleUpload = async (key: string, file: File) => {
		setUploadingKey(key);
		setStatus(null);
		try {
			const url = await uploadBagImage(file);
			updateSize(key, { image: url });
		} catch (err: any) {
			setStatus({
				variant: "error",
				msg: `Image upload failed: ${err?.response?.data?.message || err?.message || "Please try again"}`,
			});
		} finally {
			setUploadingKey(null);
		}
	};

	const handleSave = async () => {
		if (saving || isUploading) return;
		setStatus(null);
		setSaving(true);
		try {
			await saveBagContent(sizes);
			setStatus({ variant: "success", msg: "Bag content saved successfully." });
		} catch (err: any) {
			setStatus({
				variant: "error",
				msg: `Save failed: ${err?.response?.data?.message || err?.message || "Please try again"}`,
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<BookingContentWrap>
			<PageCard>
				<BookingContentName>Bag Content</BookingContentName>
				<PageSubtitle>
					Edit the title, description, and image for each bag size shown on the /book page. Changes apply
					globally across all stations.
				</PageSubtitle>

				{loading ? (
					<div style={{ textAlign: "center", padding: 48 }}>
						<CircularProgress size={36} />
					</div>
				) : (
					<>
						{sizes.map((item) => (
							<SizeRow
								key={item.key}
								item={item}
								uploading={uploadingKey === item.key}
								onTitleChange={(val) => updateSize(item.key, { title: val })}
								onDescChange={(val) => updateSize(item.key, { description: val })}
								onImageUrlChange={(val) => updateSize(item.key, { image: val })}
								onUploadFile={(file) => handleUpload(item.key, file)}
							/>
						))}

						<StickySaveRow>
							{status && <StatusMsg variant={status.variant}>{status.msg}</StatusMsg>}
							<SaveButton
								type="button"
								disabled={saving || isUploading}
								onClick={handleSave}>
								{saving ? (
									<>
										<CircularProgress
											size={14}
											style={{ color: "#fff" }}
										/>
										Saving...
									</>
								) : isUploading ? (
									"Uploading image..."
								) : (
									"Save"
								)}
							</SaveButton>
						</StickySaveRow>
					</>
				)}
			</PageCard>
		</BookingContentWrap>
	);
}
